// Overview tab — high-level health snapshot across all security
// subsystems (wave 3: Dashboard tiles, tokens, translated words).
//
// Self-contained data fetching: pulls limited slices of each source
// node in parallel on mount. This is the landing page so we accept the
// fetch cost here, but each query is bounded (limitToLast or single
// node read) and the page caches results client-side for the lifetime
// of the component mount. (Unchanged: the same five reads.)
//
// Tile click-throughs use the `onNavigate(tabIndex)` prop so the parent
// shell can switch tabs. The indices passed match the tab order in the
// SecurityDashboard shell:
//   0 = Overview, 1 = Security Logs, 2 = Staff Activity,
//   3 = Agent Performance, 4 = Service Outages, 5 = Notifications,
//   6 = Rate Limits, 7 = Geocoding API, 8 = Driver Reset, 9 = Landmarks,
//   10 = Settings.

import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ref, get, query, limitToLast } from 'firebase/database';
import { FirebaseContext } from 'common';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DashboardCard from '../../components/DashboardCard';
import CircularLoading from '../../components/CircularLoading';
import { InlineMessage, EmptyState } from '../../components/feedback';
import { IconCircle, formatNumber } from '../../components/modern';
import { tokens as c } from '../../theme';

export default function SecurityOverview({ onNavigate }) {
  const { t } = useTranslation();
  const { database } = useContext(FirebaseContext);
  const auth = useSelector((state) => state.auth);
  const isAdmin = auth?.profile?.usertype === 'admin';

  const [securityLogs, setSecurityLogs] = useState([]);
  const [serviceOutages, setServiceOutages] = useState({});
  const [notificationMetrics, setNotificationMetrics] = useState([]);
  const [geocodingStats, setGeocodingStats] = useState({});
  const [driverResetLogs, setDriverResetLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!database || !isAdmin) return;
    let cancelled = false;

    const loadAll = async () => {
      try {
        const [logsSnap, outagesSnap, metricsSnap, statsSnap, resetSnap] = await Promise.all([
          get(query(ref(database, 'security_logs'), limitToLast(100))),
          get(ref(database, 'service_outages')),
          get(query(ref(database, 'notification_metrics'), limitToLast(100))),
          get(ref(database, 'google_api_stats')),
          get(query(ref(database, 'driver_reset_logs'), limitToLast(50))),
        ]);
        if (cancelled) return;

        const logsVal = logsSnap.val();
        if (logsVal) {
          const logsArray = Object.keys(logsVal).map((key) => ({ id: key, ...logsVal[key] }));
          logsArray.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
          setSecurityLogs(logsArray);
        }

        setServiceOutages(outagesSnap.val() || {});

        const metricsVal = metricsSnap.val();
        if (metricsVal) {
          const metricsArray = Object.keys(metricsVal).map((key) => ({ id: key, ...metricsVal[key] }));
          metricsArray.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
          setNotificationMetrics(metricsArray);
        }

        setGeocodingStats(statsSnap.val() || {});

        const resetVal = resetSnap.val();
        if (resetVal) {
          const resetArray = Object.keys(resetVal).map((key) => ({ id: key, ...resetVal[key] }));
          resetArray.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
          setDriverResetLogs(resetArray);
        }
      } catch (error) {
        console.error('Failed to load overview data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAll();
    return () => {
      cancelled = true;
    };
  }, [database, isAdmin]);

  if (!isAdmin) {
    return (
      <Paper sx={{ p: 2 }}>
        <EmptyState icon={<LockOutlinedIcon />} title={t('admin_access_required', { defaultValue: 'Admin access required.' })} />
      </Paper>
    );
  }

  if (loading) return <CircularLoading inline />;

  // Derived stats — same logic as the legacy dashboard. Counts only
  // negative outcomes so the operator sees signal, not noise.
  const negativeEvents = securityLogs.filter(
    (log) =>
      log.eventType &&
      (log.eventType.includes('FAILED') ||
        log.eventType.includes('MISMATCH') ||
        log.eventType.includes('ERROR') ||
        log.eventType.includes('RATE_LIMIT') ||
        log.eventType.includes('BLOCKED') ||
        log.eventType.includes('UNAUTHORIZED') ||
        log.eventType.includes('INVALID') ||
        log.eventType.includes('EXPIRED') ||
        log.eventType.includes('DENIED') ||
        log.eventType.includes('EXCEEDED'))
  );

  const stats = {
    totalEvents: negativeEvents.length,
    rateLimitedRequests: securityLogs.filter((log) => log.eventType && (log.eventType.includes('RATE_LIMIT') || log.eventType.includes('EXCEEDED'))).length,
    failedAttempts: securityLogs.filter(
      (log) =>
        log.eventType &&
        (log.eventType.includes('FAILED') || log.eventType.includes('MISMATCH') || log.eventType.includes('ERROR') || log.eventType.includes('INVALID'))
    ).length,
    successfulBlocks: securityLogs.filter((log) => log.eventType && (log.eventType.includes('BLOCKED') || log.eventType.includes('UNAUTHORIZED'))).length,
  };

  const activeOutageCount = Object.values(serviceOutages).filter((o) => o && o.active).length;

  const pushSent = notificationMetrics.reduce((s, m) => s + (m.push?.sent || 0), 0);
  const pushFailed = notificationMetrics.reduce((s, m) => s + (m.push?.failed || 0), 0);
  const smsSent = notificationMetrics.reduce((s, m) => s + (m.sms?.sent || 0), 0);
  const smsFailed = notificationMetrics.reduce((s, m) => s + (m.sms?.failed || 0), 0);
  const totalDelivery = pushSent + pushFailed + smsSent + smsFailed;
  const totalSuccess = pushSent + smsSent;
  const notifSuccessPct = totalDelivery > 0 ? Math.round((totalSuccess / totalDelivery) * 100) : null;
  // The stats are stored under the UTC date key (as before).
  const todayKey = new Date().toISOString().split('T')[0];
  const todayGeocoding = geocodingStats[todayKey]?.total || 0;
  const recentResets = driverResetLogs.length;

  const navigate = (idx) => {
    if (typeof onNavigate === 'function') onNavigate(idx);
  };

  // A tile is one Dashboard figure; a click opens its tab.
  const tile = ({ key, icon, hue, label, value, sub, targetTab, highlight }) => (
    <ButtonBase
      key={key}
      onClick={() => navigate(targetTab)}
      aria-label={`${label}: ${value}`}
      sx={{
        display: 'block',
        textAlign: 'start',
        borderRadius: '12px',
        height: '100%',
        '&:hover .MuiPaper-root': { borderColor: c.OUTLINE_STRONG, boxShadow: '0 4px 14px rgba(15,23,42,0.08)' },
        '&.Mui-focusVisible': { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 },
      }}
    >
      <DashboardCard icon={icon} hue={hue} title={label} value={value} caption={sub} highlight={highlight} />
    </ButtonBase>
  );
  const grid4 = { display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } };
  const sectionTitle = (text) => (
    <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700, color: c.INK, mb: 1.25 }}>
      {text}
    </Typography>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Alert banner — only if something needs attention. */}
      {activeOutageCount > 0 ? (
        <InlineMessage
          kind="error"
          icon={<ReportProblemOutlinedIcon sx={{ fontSize: 20 }} />}
          title={t('security_overview_outages_active', {
            count: activeOutageCount,
            defaultValue_one: '{{count}} active service outage',
            defaultValue_other: '{{count}} active service outages',
            defaultValue: '{{count}} active service outages',
          })}
          actionLabel={t('security_overview_view', { defaultValue: 'View' })}
          onAction={() => navigate(4)}
        >
          {t('security_overview_outages_body', { defaultValue: 'One or more downstream services are degraded. Resolve from the Service Outages tab.' })}
        </InlineMessage>
      ) : null}

      {/* Security health row */}
      <Box>
        {sectionTitle(
          securityLogs.length > 0
            ? t('security_overview_security_recent', { defaultValue: 'Security (last 100 events)' })
            : t('security_overview_security', { defaultValue: 'Security' })
        )}
        <Box sx={grid4}>
          {tile({ key: 'events', icon: <ShieldOutlinedIcon />, hue: 'success', label: t('security_overview_total_events', { defaultValue: 'Total Security Events' }), value: formatNumber(stats.totalEvents), targetTab: 1 })}
          {tile({ key: 'rate', icon: <SpeedOutlinedIcon />, hue: 'amber', label: t('security_overview_rate_limited', { defaultValue: 'Rate Limited' }), value: formatNumber(stats.rateLimitedRequests), targetTab: 6 })}
          {tile({
            key: 'failed',
            icon: <WarningAmberRoundedIcon />,
            hue: stats.failedAttempts > 0 ? 'danger' : 'success',
            label: t('security_overview_failed', { defaultValue: 'Failed Attempts' }),
            value: formatNumber(stats.failedAttempts),
            targetTab: 1,
          })}
          {tile({ key: 'blocked', icon: <BlockRoundedIcon />, hue: 'info', label: t('security_overview_blocked', { defaultValue: 'Blocked Requests' }), value: formatNumber(stats.successfulBlocks), targetTab: 1 })}
        </Box>
      </Box>

      {/* Operational health row */}
      <Box>
        {sectionTitle(t('security_overview_operations', { defaultValue: 'Operations' }))}
        <Box sx={grid4}>
          {tile({
            key: 'outages',
            icon: <ReportProblemOutlinedIcon />,
            hue: activeOutageCount > 0 ? 'danger' : 'success',
            label: t('security_overview_active_outages', { defaultValue: 'Active Outages' }),
            value: formatNumber(activeOutageCount),
            sub:
              activeOutageCount === 0
                ? t('security_overview_all_healthy', { defaultValue: 'All services healthy' })
                : t('security_overview_needs_attention', { defaultValue: 'Needs attention' }),
            targetTab: 4,
            highlight: activeOutageCount > 0,
          })}
          {tile({
            key: 'notif',
            icon: <NotificationsActiveOutlinedIcon />,
            hue: notifSuccessPct === null ? 'neutral' : notifSuccessPct >= 95 ? 'success' : notifSuccessPct >= 80 ? 'amber' : 'danger',
            label: t('security_overview_notif_success', { defaultValue: 'Notification Success' }),
            value: notifSuccessPct !== null ? `${notifSuccessPct}%` : '—',
            sub:
              notifSuccessPct !== null
                ? t('security_overview_delivered', {
                    ok: formatNumber(totalSuccess),
                    total: formatNumber(totalDelivery),
                    defaultValue: '{{ok}} of {{total}} delivered (recent)',
                  })
                : t('security_overview_no_campaigns', { defaultValue: 'No recent campaigns' }),
            targetTab: 5,
          })}
          {tile({
            key: 'geo',
            icon: <MapOutlinedIcon />,
            hue: 'info',
            label: t('security_overview_geocoding_today', { defaultValue: 'Geocoding Calls Today' }),
            value: formatNumber(todayGeocoding),
            sub: 'Google Maps API',
            targetTab: 7,
          })}
          {tile({
            key: 'reset',
            icon: <AutorenewRoundedIcon />,
            hue: 'accent',
            label: t('security_overview_driver_reset', { defaultValue: 'Driver Reset Ops' }),
            value: formatNumber(recentResets),
            sub: t('security_overview_recent_ops', { defaultValue: 'Recent operations' }),
            targetTab: 8,
          })}
        </Box>
      </Box>

      {/* Staff activity quick link */}
      <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <IconCircle icon={<PersonOutlineRoundedIcon />} hue="info" size={40} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, color: c.INK }}>{t('security_overview_staff_title', { defaultValue: 'Staff Activity Monitoring' })}</Typography>
            <Typography variant="body2" sx={{ color: c.INK_2 }}>
              {t('security_overview_staff_body', { defaultValue: 'Page views, downloads, actions, and IP tracking for internal staff.' })}
            </Typography>
          </Box>
        </Box>
        <Button variant="outlined" color="neutral" startIcon={<PersonOutlineRoundedIcon />} onClick={() => navigate(2)}>
          {t('security_overview_staff_open', { defaultValue: 'Open Staff Activity' })}
        </Button>
      </Paper>
    </Box>
  );
}
